import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useMST3040_R01Store } from "./Store_MST3040_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultMST3040_R01DetailDto, MST3040_R01DetailDto } from "./Dto_MST3040_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
 
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { headerNames_MST3040_R01Detail } from "./Util_MST3040_R01";
import { useCoordinateModal } from "./hooks/useCoordinateModal";
import { useCoordinateCellChange } from "./hooks/useCoordinateCellChange";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { useGridExcelUpload } from "src/hooks/useGridExcelUpload";
import { createMST3040ExcelConfig } from "./ExcelConfig_MST3040_R01";


const COLUMN_STATE_KEY = 'MST3040_R01-detail-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<MST3040_R01DetailDto> | null>;
	defaultMST3040_R01DetailRow: MST3040_R01DetailDto;
}

export const DetailTable_MST3040_R01 = (props: Props) => {

    // [수정] props에서 defaultMST3040_R01DetailRow를 구조 분해 할당으로 가져옵니다.
    const { gridRef, defaultMST3040_R01DetailRow } = props;

    const { 
			selectedMST3040_R01, setSelectedMST3040_R01,
            MST3040_R01DetailList, setMST3040_R01DetailList, 
            editMST3040_R01Detail, setEditMST3040_R01Detail,
            selectedMST3040_R01DetailList, setSelectedMST3040_R01DetailList, removeSelectedMST3040_R01DetailList, addSelectedMST3040_R01DetailList, initSelectedMST3040_R01DetailList,
            deletedMST3040_R01DetailList, setDeletedMST3040_R01DetailList, addDeletedMST3040_R01DetailList, removeDeletedMST3040_R01DetailList,
            insertMST3040_R01DetailList, updateMST3040_R01DetailList, removeMST3040_R01DetailList,
            detailColumnState, setDetailColumnState,
            detailScrollTop, setDetailScrollTop,
            originalMST3040_R01DetailList, setOriginalMST3040_R01DetailList,
        } = useMST3040_R01Store();
    const { setCodeSearchModalProps } = useUiuxStore();
    const [clickable, setClickable] = useState(true);
    const { layerList } = useCommonCodeStore();

    const gridWrapperRef = useRef<HTMLDivElement>(null);

	const excelConfig = useMemo(
		() => createMST3040ExcelConfig(
			selectedMST3040_R01!,
			defaultMST3040_R01DetailRow,
			MST3040_R01DetailList
		),
		[selectedMST3040_R01, defaultMST3040_R01DetailRow, MST3040_R01DetailList]
	);

	const {
		fileInputRef,
		handleDownloadTemplate,
		handleUploadButtonClick,
		handleExcelUpload,
	} = useGridExcelUpload(excelConfig, MST3040_R01DetailList, setMST3040_R01DetailList);

    const {
        getRowId,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
        renderIndexCell,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: MST3040_R01DetailList,
        defaultGridData: defaultMST3040_R01DetailDto,
        selectedRow: selectedMST3040_R01DetailList,
        originalGridData: originalMST3040_R01DetailList,
        updateRow: (index: number, data: any) => updateMST3040_R01DetailList(index, data),
    });

    /**
     * 편집 가능한 컬럼 목록 정의
     * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
     */
    const defaultEditableColumns = ['coordinateCd', 'coordinateNm', 'coordinateSeq', 'xcoordinate', 'ycoordinate', 'orient', 'topBottom', 'childItemcd', 'coordinateUsage', 'afeederCd', 'bfeederCd', 'cfeederCd', 'remark'];

    /**
     * 기본 상세 테이블 이벤트 핸들러 훅
     * 
     * 주요 기능:
     * - 키다운 네비게이션 (방향키 이동)
     * - 셀 클릭/키다운 이벤트 처리
     * 
     * @param detailList - 상세 데이터 목록
     * @param editableColumns - 편집 가능한 컬럼 목록
     */
    const cellEventHandlers = useDetailTableEvents({
        detailList: MST3040_R01DetailList,
        editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
    });


	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['childItemnm', 'itemSpec', 'locationNm'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['coordinateCd', ],
		pointerCells: ['coordinateNm', 'coordinateSeq', 'xcoordinate', 'ycoordinate', 'orient', 'topBottom', 'childItemcd', 'coordinateUsage','afeederCd', 'bfeederCd', 'cfeederCd', 'remark', ],
		insertedPointerCells: ['coordinateCd',],
		commonPointerCells: [''],
		alignLeftCells: ['', ],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<MST3040_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedMST3040_R01DetailList,
            deletedList: deletedMST3040_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedMST3040_R01DetailList, deletedMST3040_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
    ]);

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {	
		InsertRowHandler({
			event: e,
			dataList: MST3040_R01DetailList,
			rowData: MST3040_R01DetailList[rowIndex],
			insertRow: insertMST3040_R01DetailList,
			defaultRowData: defaultMST3040_R01DetailRow,
			setClickable
		});
	}, [MST3040_R01DetailList, insertMST3040_R01DetailList, defaultMST3040_R01DetailRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<MST3040_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

    const baseColumnDefs: ColDef<MST3040_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(
            (params: MST3040_R01DetailDto) => renderIndexCell(
                params, 
                (innerParams: MST3040_R01DetailDto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList: deletedMST3040_R01DetailList,
                        selectedList: selectedMST3040_R01DetailList,
                        dataList: MST3040_R01DetailList
                    }),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
                (rowData) => RemoveRowHandler( 
                    gridRef,
                    MST3040_R01DetailList, 
                    rowData,
                    setClickable, 
                    removeMST3040_R01DetailList, 
                    (valueIndex) => addDeletedMST3040_R01DetailList([valueIndex.toString()])
                ),
            )
        ),
        {
            headerName: '로케이션', colId: 'coordinateCd', field: 'coordinateCd', width: 77,
            editable: (params: any) => params.data.inserted,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '로케이션명', colId: 'coordinateNm', field: 'coordinateNm', width: 135,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		{
            headerName: '좌표순번', colId: 'coordinateSeq', field: 'coordinateSeq', width: 80,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
            valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: 'X좌표', colId: 'xcoordinate', field: 'xcoordinate', width: 80,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: 'Y좌표', colId: 'ycoordinate', field: 'ycoordinate', width: 80,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: '방향', colId: 'orient', field: 'orient', width: 60,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
			headerName: 'Layer', colId: 'topBottom', field: 'topBottom', width: 77,
			...CommonCodeColumn.options(layerList, true, CommonCodeColumn.change(updateMST3040_R01DetailList), handleGridDropDownCommitAfter),
        },
        {
            headerName: '자품목코드', colId: 'childItemcd', field: 'childItemcd', width: 120,
            editable: true,
			cellRenderer: (params: any) => {
                if (!params.value && params.node.rowPinned !== 'bottom') {
                    // 값이 비어있으면 회색 기울임꼴 텍스트로 안내
                    return <span className="text-gray-400 text-[10px]">(더블클릭)</span>;
                }
                return params.value;
            },
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '자품목명', colId: 'childItemnm', field: 'childItemnm', width: 120,
            editable: false,
        },
        {
            headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
            editable: false,
        },
        {
            headerName: '소요량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 70,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: 'a피더코드', colId: 'afeederCd', field: 'afeederCd', width: 90,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: 'b피더코드', colId: 'bfeederCd', field: 'bfeederCd', width: 90,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: 'c피더코드', colId: 'cfeederCd', field: 'cfeederCd', width: 90,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 77,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		// ...IndexParameterCell(),
    ], [deletedMST3040_R01DetailList, MST3040_R01DetailList]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs]);

    const columnDefs: ColDef<MST3040_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }
        
        return GridCustomColumnDefs<MST3040_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames: headerNames_MST3040_R01Detail,
            fallbackHeaderClass: aggridHeaderClass
        });
    }, [baseColumnDefs, detailColumnState, headerNames_MST3040_R01Detail]);

	const { settingCodeSearchModalProps, handlePopupClose } = useCoordinateModal({
		updateMST3040_R01Detail: updateMST3040_R01DetailList,
		gridRef,
		MST3040_R01DetailList,
	});

	const { handleCellChange, handleChildItemCodeSearch } = useCoordinateCellChange({
		MST3040_R01DetailList,
		updateMST3040_R01DetailList,
		originalMST3040_R01DetailList,
		settingCodeSearchModalProps,
		gridRef,
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: MST3040_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedMST3040_R01DetailList,
        deletedList: deletedMST3040_R01DetailList,
        addSelected: addSelectedMST3040_R01DetailList,
        removeSelected: removeSelectedMST3040_R01DetailList,
        clickable, setClickable
    });

	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<MST3040_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => (field === 'childItemcd' || field === 'childItemnm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'BOM_자품목',
					foundData.childItemcd || '',
					false,
					'childItemcd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'afeederCd' || field === 'afeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Afeeder',
					foundData.afeederCd || '',
					false,
					'afeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'bfeederCd' || field === 'bfeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Bfeeder',
					foundData.bfeederCd || '',
					false,
					'bfeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
		{
			validate: (field, data) => (field === 'cfeederCd' || field === 'cfeederNm'),
			action: (e, foundData) => {
				settingCodeSearchModalProps(
					'Cfeeder',
					foundData.cfeederCd || '',
					false,
					'cfeederCd',
					foundData,
					foundData.valueIndex!
				);
			}
		},
	], [settingCodeSearchModalProps]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(MST3040_R01DetailList, 'valueIndex', cellDoubleClickActions);


    const handlePostSort = useCallback((params: PostSortRowsParams<MST3040_R01DetailDto>) => {
        postSortHandler(params, MST3040_R01DetailList, originalMST3040_R01DetailList, setMST3040_R01DetailList);
    }, [MST3040_R01DetailList, originalMST3040_R01DetailList, ]);

    const pinnedBottomRowData = useMemo(() => {

        return [{
            coordinateCd: MST3040_R01DetailList.length.toLocaleString() + "건",
        }];      
    }, [MST3040_R01DetailList, deletedMST3040_R01DetailList,]);

    return (
        <>
            <div className="w-full h-full flex flex-col gap-[4px]">
				
				<div className="flex justify-start gap-2 mb-1">
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleExcelUpload}
						style={{ display: 'none' }}
						accept=".xlsx, .xls"
					/>
					<button 
						className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-sm" 
						onClick={handleDownloadTemplate}
						title="엑셀 업로드 양식 다운로드"
					>
						템플릿 다운로드
					</button>
					<button 
						className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm" 
						onClick={handleUploadButtonClick}
					>
						Excel 업로드
					</button>
				</div>

				<GridContextWrapper<MST3040_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					enableCellCalculation
					setPaddedGridData={setMST3040_R01DetailList}
					initSelected={initSelectedMST3040_R01DetailList}
					defaultRowData={defaultMST3040_R01DetailRow}
					baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<MST3040_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						defaultColDef={cellCssDefaultColDef}

						//  새로운 훅 구조와 호환
						// disableBuiltinCellSelection={true} // 내장 셀 선택 비활성화 (새로운 훅 사용)
						columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
						setColumnState={setDetailColumnState}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}

						// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
						rowClassRules={createRowClassRules<MST3040_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedMST3040_R01DetailList,
							deletedList: deletedMST3040_R01DetailList
						})}

						// 이벤트 핸들러들
						onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
						onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
						onCellDoubleClicked={handleCellDoubleClick}                     // 셀 더블클릭 (코드 검색)
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}             // 셀 키다운 (네비게이션)
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						
						// 정렬 후 처리
						onPostSortRows={handlePostSort}
					/>
				</GridContextWrapper>
            </div>
        </>
    )
}
